import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Layers, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import ProposalCardShell from './ProposalCardShell';
import SegmentRuleList from './SegmentRuleList';
import { segmentsApi } from '@/services/segments-api';
import { handleApiError } from '@/services/api';
import type { ToolSegmentPayload } from '@/types/chat';

type Proposal = Extract<ToolSegmentPayload, { type: 'segment_proposal' }>;

const SegmentProposalCard: React.FC<{ proposal: Proposal }> = ({ proposal }) => {
  const [dismissed, setDismissed] = useState(false);
  const [created, setCreated] = useState(false);
  const { proposed, preview } = proposal;

  const approve = useMutation({
    mutationFn: () =>
      segmentsApi.create({
        data_source_account_id: proposed.data_source_account_id,
        key: proposed.key,
        name: proposed.name,
        definition: proposed.definition,
      }),
    onSuccess: () => {
      setCreated(true);
      toast.success('Segment created');
    },
    onError: (error) => toast.error(handleApiError(error) || 'Failed to create segment'),
  });

  if (dismissed) {
    return (
      <ProposalCardShell icon={Layers} kicker="Segment proposal" title={proposed.name} actions={null} resolved={{ tone: 'muted', label: 'Dismissed' }}>
        <div />
      </ProposalCardShell>
    );
  }

  if (created) {
    return (
      <ProposalCardShell icon={Layers} kicker="Segment proposal" title={proposed.name} actions={null} resolved={{ tone: 'success', label: '✓ Segment created' }}>
        <div />
      </ProposalCardShell>
    );
  }

  return (
    <ProposalCardShell
      icon={Layers}
      kicker="Segment proposal"
      title={proposed.name}
      subtitle={`key: ${proposed.key}`}
      banner={
        preview.warnings.length > 0 ? (
          <div className="rounded-lg border border-warn/20 bg-warn/15 p-3 text-xs text-warn flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <ul className="space-y-1 flex-1">
              {preview.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        ) : null
      }
      actions={
        <>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={approve.isPending}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={() => approve.mutate()}
            disabled={approve.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {approve.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Approve &amp; create
          </button>
        </>
      }
    >
      <div className="rounded-lg bg-muted dark:bg-white/[0.03] border border-border p-3 text-sm text-foreground font-medium">
        Matches {preview.matched_items} item{preview.matched_items === 1 ? '' : 's'}
        {preview.weighted_count > 0 && (
          <span className="text-muted-foreground font-normal">
            {' '}
            ({preview.full_weight_count} full, {preview.weighted_count} split)
          </span>
        )}
      </div>

      <SegmentRuleList label="Match rules" rules={proposed.definition.match} />
      <SegmentRuleList label="Exclude rules" rules={proposed.definition.exclude ?? []} />
    </ProposalCardShell>
  );
};

export default SegmentProposalCard;
